import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { Plus, Target, Check, Sparkles } from 'lucide-react';
import { brandColors } from '@/design';
import type { RealTimeExtraction } from '../../types';

export interface ManualTask {
  id: string;
  text: string;
  completed: boolean;
}

interface SessionTaskPanelProps {
  extractions: RealTimeExtraction[];
  manualTasks: ManualTask[];
  onAddTask: (text: string) => void;
  onToggleTask: (id: string) => void;
}

export default function SessionTaskPanel({
  extractions,
  manualTasks,
  onAddTask,
  onToggleTask,
}: SessionTaskPanelProps) {
  const [inputText, setInputText] = useState('');
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const actionItems = extractions.filter((e) => e.type === 'action_item');

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [actionItems.length, manualTasks.length]);

  function handleAdd() {
    const text = inputText.trim();
    if (!text) return;
    onAddTask(text);
    setInputText('');
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
    if (e.key === 'Escape') {
      setShowInput(false);
      setInputText('');
    }
  }

  const hasItems = actionItems.length > 0 || manualTasks.length > 0;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Target size={14} style={{ color: brandColors.teal }} />
          <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            Tasks
          </span>
        </div>
        <span className="text-xs text-[var(--color-text-muted)]">
          {actionItems.length + manualTasks.length}
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2">
        {!hasItems && (
          <div className="py-8 text-center">
            <Sparkles size={20} className="mx-auto mb-2 opacity-30" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-xs text-[var(--color-text-muted)]">
              Tasks will appear here as you discuss them
            </p>
          </div>
        )}

        {actionItems.map((item, i) => (
          <div
            key={`ai-${i}`}
            className="flex items-start gap-2.5 px-2 py-2 rounded-lg text-left w-full"
          >
            <div
              className="w-4 h-4 rounded flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: `${brandColors.teal}22`, border: `1.5px solid ${brandColors.teal}60` }}
            >
              <Sparkles size={8} style={{ color: brandColors.teal }} />
            </div>
            <span className="text-xs leading-snug text-[var(--color-text-primary)]">
              {item.content}
            </span>
          </div>
        ))}

        {manualTasks.map((task) => (
          <button
            key={task.id}
            onClick={() => onToggleTask(task.id)}
            className="flex items-start gap-2.5 px-2 py-2 rounded-lg hover:bg-[var(--color-surface-elevated)] transition-colors text-left w-full"
          >
            <div
              className="w-4 h-4 rounded flex items-center justify-center shrink-0 mt-0.5 transition-colors"
              style={{
                background: task.completed ? 'var(--color-success)' : 'transparent',
                border: task.completed ? 'none' : '1.5px solid var(--color-border)',
              }}
            >
              {task.completed && <Check size={8} color="white" strokeWidth={3} />}
            </div>
            <span
              className={`text-xs leading-snug ${
                task.completed
                  ? 'text-[var(--color-text-muted)] line-through'
                  : 'text-[var(--color-text-primary)]'
              }`}
            >
              {task.text}
            </span>
          </button>
        ))}
      </div>

      <div className="px-3 py-3 border-t border-[var(--color-border)] shrink-0">
        {showInput ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                if (!inputText.trim()) setShowInput(false);
              }}
              placeholder="Add a task…"
              className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-border-focus)] transition-colors"
            />
            <button
              onClick={handleAdd}
              disabled={!inputText.trim()}
              className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-40"
              style={{ background: 'var(--color-brand-primary)' }}
            >
              <Plus size={12} color="white" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowInput(true)}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text-secondary)] transition-colors"
          >
            <Plus size={12} />
            Add task manually
          </button>
        )}
      </div>
    </div>
  );
}
